import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  MenuItem,
  FormGroup,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../../api/client';

const initialCompany = {
  name: '',
  description: '',
  website: '',
  logo: null,
  email: '',
  phone: '',
  fax: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  industry: '',
  company_size: '',
  profile_picture: '',
  founded_year: '',
  status: 'active',
  subscription_plan: 'basic',
  subscription_end_date: '',
  available_languages: [],
  default_language: '',
  settings: {
    interview_settings: {
      allow_video_recording: false,
      max_interviews_per_day: 1,
      require_aadhaar_verification: false
    },
    notification_settings: {
      email_notifications: false,
      interview_reminders: false
    }
  }
};

export default function AddEditCompanyDialog({ open, mode, existingCompany = null, onSubmit, onClose }) {
  const [company, setCompany] = useState(initialCompany);
  const [languages, setLanguages] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingLanguages, setLoadingLanguages] = useState(false);

  useEffect(() => {
    const fetchLanguages = async () => {
      setLoadingLanguages(true);
      try {
        const response = await api.get('/languages/');
        setLanguages(response.data.results || response.data || []);
      } catch (error) {
        console.error('Error fetching languages:', error);
      } finally {
        setLoadingLanguages(false);
      }
    };
    if (open) {
      fetchLanguages();
    }
  }, [open]);

  useEffect(() => {
    if (mode === 'Edit' && existingCompany) {
      setCompany({
        ...existingCompany,
        available_languages: existingCompany.available_languages || [],
        default_language: existingCompany.default_language || '',
      });
    } else {
      setCompany(initialCompany);
    }
  }, [mode, existingCompany]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    const keys = name.split('.');

    setCompany(prev => {
      const updated = { ...prev };

      if (type === 'file') {
        updated[name] = files[0]; // file input
      } else if (keys.length === 1) {
        updated[name] = type === 'checkbox' ? checked : value;
      } else if (keys.length === 2) {
        updated[keys[0]][keys[1]] = type === 'checkbox' ? checked : value;
      } else if (keys.length === 3) {
        updated[keys[0]][keys[1]][keys[2]] = type === 'checkbox' ? checked : value;
      }

      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();
    for (const key in company) {
      if (company[key] instanceof File) {
        formData.append(key, company[key]);
      } else if (typeof company[key] === 'object') {
        formData.append(key, JSON.stringify(company[key]));
      } else {
        formData.append(key, company[key]);
      }
    }

    onSubmit(formData);
  };

  const handleClose = () => {
    onClose();
  };

  const fields = [
    { name: 'name', label: 'Company Name', required: true },
    { name: 'description', label: 'Description' },
    { name: 'website', label: 'Website' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'phone', label: 'Phone' },
    { name: 'fax', label: 'Fax' },
    { name: 'address_line1', label: 'Address Line 1' },
    { name: 'address_line2', label: 'Address Line 2' },
    { name: 'city', label: 'City' },
    { name: 'state', label: 'State' },
    { name: 'country', label: 'Country' },
    { name: 'postal_code', label: 'Postal Code' },
    { name: 'industry', label: 'Industry' },
    { name: 'company_size', label: 'Company Size' },
    { name: 'logo', label: 'Company Logo', type: 'file' },
    { name: 'founded_year', label: 'Founded Year', type: 'number' },
    { name: 'subscription_end_date', label: 'Subscription End Date', type: 'date' },
  ];

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 0,
          maxHeight: '90vh',
          minWidth: 350,
          maxWidth: 1000,
          width: '90%',
          mx: 'auto',
          mt: '5vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 4, py: 2, borderBottom: '1px solid #ddd', position: 'relative' }}>
          <Typography variant="h5">
            {mode === 'Edit' ? 'Edit Company' : 'Add New Company'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', top: 12, right: 12, color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            overflowY: 'auto',
            px: 4,
            py: 3,
            flexGrow: 1,
          }}
        >
          <form onSubmit={handleSubmit}>
            {/* Group Fields in Rows of 3 */}
            {(() => {
              const rows = [];
              for (let i = 0; i < fields.length; i += 3) {
                const rowFields = fields.slice(i, i + 3);
                rows.push(
                  <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                    {rowFields.map(({ name, label, type = 'text', required }) => (
                      type === 'file' ? (
                        <div key={name} style={{ flex: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>{label}</Typography>
                          <input
                            type="file"
                            name={name}
                            accept="image/*"
                            onChange={handleChange}
                          />
                          {company[name] && typeof company[name] === 'object' && (
                            <img
                              src={URL.createObjectURL(company[name])}
                              alt="Profile Preview"
                              style={{
                                marginTop: 8,
                                width: 80,
                                height: 80,
                                objectFit: 'cover',
                                borderRadius: '50%'
                              }}
                            />
                          )}
                        </div>
                      ) : (
                        <TextField
                          key={name}
                          fullWidth
                          name={name}
                          label={label}
                          type={type}
                          required={required}
                          value={company[name] || ''}
                          onChange={handleChange}
                        />
                      )
                    ))}
                  </div>
                );
              }
              return rows;
            })()}

            {/* Dropdowns */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <TextField
                select
                fullWidth
                name="status"
                label="Status"
                value={company.status}
                onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>

              <TextField
                select
                fullWidth
                name="subscription_plan"
                label="Subscription Plan"
                value={company.subscription_plan}
                onChange={handleChange}
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </TextField>
            </div>

            {/* Language Selection */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <FormControl fullWidth>
                <InputLabel>Available Languages</InputLabel>
                <Select
                  multiple
                  name="available_languages"
                  value={company.available_languages || []}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCompany(prev => ({
                      ...prev,
                      available_languages: typeof value === 'string' ? value.split(',') : value,
                      // Clear default language if it's not in selected languages
                      default_language: prev.default_language && !value.includes(prev.default_language) ? '' : prev.default_language
                    }));
                  }}
                  input={<OutlinedInput label="Available Languages" />}
                  renderValue={(selected) => {
                    const selectedLangs = languages.filter(lang => selected.includes(lang.code));
                    return selectedLangs.map(lang => lang.name).join(', ');
                  }}
                >
                  {languages.map((language) => (
                    <MenuItem key={language.code} value={language.code}>
                      <Checkbox checked={(company.available_languages || []).indexOf(language.code) > -1} />
                      <ListItemText
                        primary={`${language.name} (${language.native_name})`}
                        secondary={language.code}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Default Language</InputLabel>
                <Select
                  name="default_language"
                  value={company.default_language || ''}
                  onChange={handleChange}
                  label="Default Language"
                  disabled={(company.available_languages || []).length === 0}
                >
                  <MenuItem value="">None</MenuItem>
                  {languages
                    .filter(lang => (company.available_languages || []).length === 0 || (company.available_languages || []).includes(lang.code))
                    .map((language) => (
                      <MenuItem key={language.code} value={language.code}>
                        {language.name} ({language.native_name})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>

            {/* Interview Settings */}
            <Typography variant="h6" gutterBottom>Interview Settings</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="settings.interview_settings.allow_video_recording"
                    checked={company?.settings?.interview_settings?.allow_video_recording}
                    onChange={handleChange}
                  />
                }
                label="Allow Video Recording"
              />
              <TextField
                name="settings.interview_settings.max_interviews_per_day"
                label="Max Interviews per Day"
                type="number"
                value={company?.settings?.interview_settings?.max_interviews_per_day}
                onChange={handleChange}
                sx={{ mt: 2, maxWidth: 300 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="settings.interview_settings.require_aadhaar_verification"
                    checked={company?.settings?.interview_settings?.require_aadhaar_verification}
                    onChange={handleChange}
                  />
                }
                label="Require Aadhaar Verification"
              />
            </FormGroup>

            {/* Notification Settings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Notification Settings</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="settings.notification_settings.email_notifications"
                    checked={company?.settings?.notification_settings?.email_notifications}
                    onChange={handleChange}
                  />
                }
                label="Email Notifications"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="settings.notification_settings.interview_reminders"
                    checked={company?.settings?.notification_settings?.interview_reminders}
                    onChange={handleChange}
                  />
                }
                label="Interview Reminders"
              />
            </FormGroup>

            {/* Submit */}
            <Box sx={{ mt: 3 }}>
              <Button type="submit" variant="contained" color="primary">
                {mode === 'Edit' ? 'Update Company' : 'Add Company'}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Modal>
  );
}
